import { useState } from 'react'
import { tierLabel, type Cut, type Gate, type ScoringModel, type TierRule, type TierWhen } from '../lib/api'
import { Status, Toggletip } from './kit'
import { ReasonTip } from './vendor/ReasonTip'
import '../styles/sweep.css'

export const BANDS = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'] as const

// The same four. The CHECK on risk_model_versions.provisional_ceiling permits
// LOW, MEDIUM, HIGH and CRITICAL and nothing else.
//
// This used to offer INSUFFICIENT_EVIDENCE as well, and EMPTY_MODEL used it as
// the default, so a new risk model authored in the Studio carried a ceiling the
// database would refuse. INSUFFICIENT_EVIDENCE is a band an assessment can end
// up in when there is not enough evidence to say anything; it is not a ceiling
// a model can be capped at, because capping at it would say every provisional
// assessment is unreadable rather than that it is unattested.
export const CEILINGS = BANDS

// The four the engine implements. Two change a band, two stop an approval, and
// none of them can ever make a band better: worse() is applied on every gate,
// so a gate is only ever a brake. Each detail is the one line under the effect
// field, so each stays inside 80 characters.
export const EFFECTS: { value: string; label: string; needsBand: boolean; detail: string }[] = [
  {
    value: 'CAP_AT_BAND',
    label: 'Cap at a band',
    needsBand: true,
    detail: 'No better than this band: a better computed band is pulled down to it',
  },
  {
    value: 'FORCE_BAND',
    label: 'Force a band',
    needsBand: true,
    detail: 'The band is set to this outright, whatever the weights said',
  },
  {
    value: 'BLOCK_APPROVAL',
    label: 'Block approval',
    needsBand: false,
    detail: 'Band untouched, and the assessment cannot be approved until this is dealt with',
  },
  {
    value: 'REQUIRE_EXCEPTION',
    label: 'Require an exception',
    needsBand: false,
    detail: 'Approval needs a written, time-boxed exception naming a compensating control',
  },
]

const EFFECT_NEEDS_BAND = new Set(EFFECTS.filter((e) => e.needsBand).map((e) => e.value))

export const EMPTY_MODEL: ScoringModel = {
  tier_rules: [{ when: { always: true }, tier: 3, why: 'Everything not otherwise matched' }],
  weights: {},
  band_matrix: {},
  gates: [],
  coverage_floor: 70,
  // MEDIUM, matching the schema default and what the API substitutes when this
  // arrives empty.
  provisional_ceiling: 'MEDIUM',
}

// Named conditions rather than a free expression. Every one of these maps onto
// a column the vendor profile already carries, which is why a tier rule can be
// re-evaluated years later without the code that wrote it.
const TIER_CONDITIONS: { key: keyof TierWhen; label: string; kind: 'list' | 'bool' | 'always' }[] = [
  { key: 'data_categories_any', label: 'Data categories include any of', kind: 'list' },
  { key: 'volume_band_in', label: 'Record volume band is one of', kind: 'list' },
  { key: 'system_access_in', label: 'System access is one of', kind: 'list' },
  { key: 'regulated_process', label: 'Supports a regulated process', kind: 'bool' },
  { key: 'material_outsourcing', label: 'Is material outsourcing', kind: 'bool' },
  { key: 'cross_border', label: 'Involves a cross-border transfer', kind: 'bool' },
  { key: 'subcontractors_declared', label: 'Declares subcontractors', kind: 'bool' },
  { key: 'always', label: 'Always (the catch-all)', kind: 'always' },
]

type Props = {
  model: ScoringModel
  onChange: (next: ScoringModel) => void
  editable: boolean
  // Keys the questionnaires bound to this model actually ask. A weight naming
  // anything else scores nothing and is the single most common way a model
  // silently under-scores a vendor.
  knownKeys: string[]
  unweighted: string[]
  // What each question actually asks, by key, so a weight is set against a
  // sentence rather than a code.
  prompts?: Record<string, string>
}

export function RiskModelEditor({ model, onChange, editable, knownKeys, unweighted, prompts }: Props) {
  const known = new Set(knownKeys)

  function set(patch: Partial<ScoringModel>) {
    onChange({ ...model, ...patch })
  }

  return (
    <div className="studio">
      <Floors model={model} set={set} editable={editable} />
      <TierRules model={model} set={set} editable={editable} />
      <BandMatrix model={model} set={set} editable={editable} />
      <Weights model={model} set={set} editable={editable} known={known} unweighted={unweighted} prompts={prompts} />
      <Gates model={model} set={set} editable={editable} known={known} knownKeys={knownKeys} />
    </div>
  )
}

type PartProps = {
  model: ScoringModel
  set: (patch: Partial<ScoringModel>) => void
  editable: boolean
}

function Floors({ model, set, editable }: PartProps) {
  return (
    <section className="panel">
      <h2>Coverage floor and provisional ceiling</h2>
      <div className="form-grid">
        <div className="field">
          <label htmlFor="coverage-floor">Coverage floor</label>
          <input
            id="coverage-floor"
            type="number"
            min={0}
            max={100}
            step={1}
            disabled={!editable}
            value={model.coverage_floor}
            onChange={(e) => set({ coverage_floor: Number(e.target.value) })}
            aria-describedby="coverage-floor-note"
          />
          <p className="field-note" id="coverage-floor-note">
            Below {model.coverage_floor}% of the weight answered, the band is held at the ceiling{' '}
            <Toggletip label="How the coverage floor is read">
              A percentage, compared against coverage directly. Below it no final band is reachable and the reading is
              held at the provisional ceiling beside this.
            </Toggletip>
          </p>
        </div>
        <div className="field">
          <label htmlFor="prov-ceiling">Provisional ceiling</label>
          <select
            id="prov-ceiling"
            disabled={!editable}
            value={model.provisional_ceiling}
            onChange={(e) => set({ provisional_ceiling: e.target.value })}
          >
            {CEILINGS.map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </select>
        </div>
      </div>
    </section>
  )
}

function TierRules({ model, set, editable }: PartProps) {
  function edit(i: number, fn: (r: TierRule) => TierRule) {
    set({ tier_rules: model.tier_rules.map((r, n) => (n === i ? fn(r) : r)) })
  }

  return (
    <section className="panel">
      <h2>Inherent tier rules</h2>

      {model.tier_rules.map((rule, i) => (
        <div className="matrix-tier" key={i}>
          <div className="gate-head">
            <div className="field">
              <label htmlFor={'tr-tier-' + i}>Tier</label>
              <select
                id={'tr-tier-' + i}
                disabled={!editable}
                value={rule.tier}
                onChange={(e) => edit(i, (r) => ({ ...r, tier: Number(e.target.value) }))}
              >
                {[1, 2, 3, 4].map((t) => (
                  <option key={t} value={t}>
                    {tierLabel(t)}
                  </option>
                ))}
              </select>
            </div>
            <div className="field studio-grow">
              <label htmlFor={'tr-why-' + i}>Why</label>
              <input
                id={'tr-why-' + i}
                disabled={!editable}
                value={rule.why ?? ''}
                onChange={(e) => edit(i, (r) => ({ ...r, why: e.target.value }))}
                aria-describedby={'tr-why-note-' + i}
              />
              <p className="field-note" id={'tr-why-note-' + i}>
                Shown on the vendor detail screen as why a vendor is {tierLabel(rule.tier)}
              </p>
            </div>
            {editable && (
              <div className="studio-section-actions">
                <button
                  type="button"
                  disabled={i === 0}
                  onClick={() => set({ tier_rules: swap(model.tier_rules, i, i - 1) })}
                  aria-label={'Move rule ' + (i + 1) + ' earlier'}
                >
                  Up
                </button>
                <button
                  type="button"
                  disabled={i === model.tier_rules.length - 1}
                  onClick={() => set({ tier_rules: swap(model.tier_rules, i, i + 1) })}
                  aria-label={'Move rule ' + (i + 1) + ' later'}
                >
                  Down
                </button>
                <button
                  type="button"
                  className="danger"
                  onClick={() => set({ tier_rules: model.tier_rules.filter((_, n) => n !== i) })}
                  aria-label={'Remove rule ' + (i + 1)}
                >
                  Remove
                </button>
              </div>
            )}
          </div>

          <fieldset className="fieldset">
            <legend>Matches when</legend>
            {TIER_CONDITIONS.map((c) => (
              <TierCondition
                key={String(c.key)}
                cond={c}
                id={i + '-' + String(c.key)}
                when={rule.when}
                editable={editable}
                onChange={(when) => edit(i, (r) => ({ ...r, when }))}
              />
            ))}
          </fieldset>
        </div>
      ))}

      {editable && (
        <button
          type="button"
          onClick={() =>
            set({
              tier_rules: [...model.tier_rules, { when: {}, tier: 2, why: '' }],
            })
          }
        >
          Add a tier rule
        </button>
      )}
    </section>
  )
}

function TierCondition({
  cond,
  id,
  when,
  editable,
  onChange,
}: {
  cond: (typeof TIER_CONDITIONS)[number]
  id: string
  when: TierWhen
  editable: boolean
  onChange: (when: TierWhen) => void
}) {
  const raw = when[cond.key]

  if (cond.kind === 'list') {
    const list = Array.isArray(raw) ? raw : []
    return (
      <div className="field">
        <label htmlFor={'tc-' + id}>{cond.label}</label>
        <input
          id={'tc-' + id}
          className="mono"
          disabled={!editable}
          value={list.join(', ')}
          spellCheck={false}
          placeholder="leave blank to ignore this condition"
          onChange={(e) => {
            const next = e.target.value
              .split(',')
              .map((v) => v.trim().toUpperCase())
              .filter(Boolean)
            const w = { ...when }
            if (next.length === 0) delete w[cond.key]
            else Object.assign(w, { [cond.key]: next })
            onChange(w)
          }}
        />
      </div>
    )
  }

  // Three states, not two. Unset means the condition is not part of this rule
  // at all, which is a different statement from requiring it to be false.
  const value = raw === undefined ? 'IGNORE' : raw ? 'TRUE' : 'FALSE'
  return (
    <div className="field">
      <label htmlFor={'tc-' + id}>{cond.label}</label>
      <select
        id={'tc-' + id}
        disabled={!editable}
        value={value}
        onChange={(e) => {
          const w = { ...when }
          if (e.target.value === 'IGNORE') delete w[cond.key]
          else Object.assign(w, { [cond.key]: e.target.value === 'TRUE' })
          onChange(w)
        }}
      >
        <option value="IGNORE">Not part of this rule</option>
        <option value="TRUE">Must be true</option>
        {cond.kind !== 'always' && <option value="FALSE">Must be false</option>}
      </select>
    </div>
  )
}

function BandMatrix({ model, set, editable }: PartProps) {
  const tiers = [1, 2, 3, 4]

  function setCuts(tier: number, cuts: Cut[]) {
    set({ band_matrix: { ...model.band_matrix, [String(tier)]: cuts } })
  }

  return (
    <section className="panel">
      {/* How a cut reads is the rule for the whole panel, so it is one line and
          an (i). The tier names in the reason are the tenant's own labels,
          whose length the screen does not control, so the tip is a ReasonTip. */}
      <h2>Band matrix</h2>
      <p className="sw-help">
        One row per tier, cuts read low to high and the last one should reach 100{' '}
        <ReasonTip
          label="How a cut works"
          text={
            'A cut sends a control score (0 to 100) up to its value into its band. The same score means a different thing for a ' +
            tierLabel(4) +
            ' vendor than a ' +
            tierLabel(1) +
            ' one, which is why each tier has a row.'
          }
        />
      </p>

      {tiers.map((tier) => {
        const cuts = model.band_matrix[String(tier)] ?? []
        return (
          <div className="matrix-tier" key={tier}>
            <h3>{tierLabel(tier)}</h3>
            {cuts.length === 0 && (
              <p className="muted sw-row">
                <Status tone="warn">No cuts</Status>
                <span>A vendor in this tier cannot be banded, the score comes back insufficient</span>
              </p>
            )}
            <div className="matrix-cuts">
              {cuts.map((cut, ci) => (
                <div className="matrix-cut" key={ci}>
                  <div className="field">
                    <label htmlFor={'cut-max-' + tier + '-' + ci}>Up to</label>
                    <input
                      id={'cut-max-' + tier + '-' + ci}
                      type="number"
                      min={0}
                      max={100}
                      step={1}
                      disabled={!editable}
                      value={cut.max}
                      onChange={(e) =>
                        setCuts(
                          tier,
                          cuts.map((c, n) => (n === ci ? { ...c, max: Number(e.target.value) } : c)),
                        )
                      }
                    />
                  </div>
                  <div className="field">
                    <label htmlFor={'cut-band-' + tier + '-' + ci}>Band</label>
                    <select
                      id={'cut-band-' + tier + '-' + ci}
                      disabled={!editable}
                      value={cut.band}
                      onChange={(e) =>
                        setCuts(
                          tier,
                          cuts.map((c, n) => (n === ci ? { ...c, band: e.target.value } : c)),
                        )
                      }
                    >
                      {BANDS.map((b) => (
                        <option key={b} value={b}>
                          {b}
                        </option>
                      ))}
                    </select>
                  </div>
                  {editable && (
                    <button
                      type="button"
                      className="danger"
                      onClick={() => setCuts(tier, cuts.filter((_, n) => n !== ci))}
                      aria-label={'Remove the ' + cut.band + ' cut from ' + tierLabel(tier)}
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
              {editable && (
                <button type="button" onClick={() => setCuts(tier, [...cuts, { max: 100, band: 'LOW' }])}>
                  Add a cut to {tierLabel(tier)}
                </button>
              )}
            </div>
          </div>
        )
      })}
    </section>
  )
}

function Weights({
  model,
  set,
  editable,
  known,
  unweighted,
  prompts,
}: PartProps & { known: Set<string>; unweighted: string[]; prompts?: Record<string, string> }) {
  const [newKey, setNewKey] = useState('')
  const entries = Object.entries(model.weights).sort(([a], [b]) => a.localeCompare(b))
  const orphans = entries.filter(([k]) => known.size > 0 && !known.has(k))

  function setWeight(key: string, weight: number) {
    set({ weights: { ...model.weights, [key]: weight } })
  }

  function removeWeight(key: string) {
    const next = { ...model.weights }
    delete next[key]
    set({ weights: next })
  }

  return (
    <section className="panel">
      <h2>Weights</h2>

      {orphans.length > 0 && (
        <div className="issue-list" role="alert">
          <h3>These weights name a question no bound questionnaire asks</h3>
          <ul>
            {orphans.map(([k]) => (
              <li key={k}>
                <span className="mono">{k}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {unweighted.length > 0 && (
        <div className="note">
          <strong>
            {unweighted.length} question{unweighted.length === 1 ? '' : 's'} carry no weight in this model
          </strong>{' '}
          <span className="mono">{unweighted.slice(0, 12).join(', ')}</span>
          {unweighted.length > 12 && <span className="muted"> and {unweighted.length - 12} more</span>}
        </div>
      )}

      {entries.length === 0 ? (
        <div className="empty">
          <strong>No weights</strong>
          Every question scores nothing, so every vendor gets the same control score
        </div>
      ) : (
        <div>
          {entries.map(([key, weight]) => (
            <div className={known.size > 0 && !known.has(key) ? 'weight-row weight-unused' : 'weight-row'} key={key}>
              {/* The question, and then its code. Weighting a bare PR-01 asks
                  somebody to hold the questionnaire in their head, which is
                  why the weights looked unusable from the questionnaire side.
                  The code stays because it is what the model is keyed on and
                  what an error message names. */}
              <span className="weight-what">
                <strong>{prompts?.[key] ?? 'A question no bound questionnaire asks'}</strong>
                <span className="cell-sub mono">{key}</span>
              </span>
              {known.size > 0 && !known.has(key) && <Status tone="crit">Not asked</Status>}
              <label className="vh" htmlFor={'w-' + key}>
                Weight for {key}
              </label>
              <input
                id={'w-' + key}
                type="number"
                min={0}
                step={0.5}
                disabled={!editable}
                value={weight}
                onChange={(e) => setWeight(key, Number(e.target.value))}
              />
              {editable && (
                <button type="button" className="danger" onClick={() => removeWeight(key)} aria-label={'Remove weight ' + key}>
                  Remove
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {editable && (
        <div className="pickrow">
          <div className="field studio-grow">
            <label htmlFor="new-weight-key">Add a weight</label>
            <input
              id="new-weight-key"
              className="mono"
              list="known-question-keys"
              value={newKey}
              spellCheck={false}
              onChange={(e) => setNewKey(e.target.value)}
            />
            <datalist id="known-question-keys">
              {[...known].sort().map((k) => (
                <option key={k} value={k} />
              ))}
            </datalist>
          </div>
          <button
            type="button"
            disabled={!newKey.trim() || model.weights[newKey.trim()] !== undefined}
            onClick={() => {
              setWeight(newKey.trim(), 1)
              setNewKey('')
            }}
          >
            Add
          </button>
        </div>
      )}
    </section>
  )
}

function Gates({
  model,
  set,
  editable,
  known,
  knownKeys,
}: PartProps & { known: Set<string>; knownKeys: string[] }) {
  function edit(i: number, fn: (g: Gate) => Gate) {
    set({ gates: model.gates.map((g, n) => (n === i ? fn(g) : g)) })
  }

  return (
    <section className="panel">
      <h2>Gates</h2>

      {model.gates.map((gate, i) => {
        const needsBand = EFFECT_NEEDS_BAND.has(gate.effect)
        const unknownKey = known.size > 0 && gate.question_key !== '' && !known.has(gate.question_key)
        return (
          <div className="gate-card" key={i}>
            <div className="gate-head">
              <div className="field">
                <label htmlFor={'g-key-' + i}>Gate key</label>
                <input
                  id={'g-key-' + i}
                  className="mono"
                  disabled={!editable}
                  value={gate.key}
                  spellCheck={false}
                  onChange={(e) => edit(i, (g) => ({ ...g, key: e.target.value }))}
                />
              </div>
              <div className="field">
                <label htmlFor={'g-q-' + i}>Question</label>
                <input
                  id={'g-q-' + i}
                  className="mono"
                  disabled={!editable}
                  list="known-question-keys-gate"
                  value={gate.question_key}
                  spellCheck={false}
                  onChange={(e) => edit(i, (g) => ({ ...g, question_key: e.target.value }))}
                />
                {unknownKey && <p className="field-error">No bound questionnaire asks this, so the gate can never fire</p>}
              </div>
              {editable && (
                <div className="studio-section-actions">
                  <button
                    type="button"
                    className="danger"
                    onClick={() => set({ gates: model.gates.filter((_, n) => n !== i) })}
                    aria-label={'Remove gate ' + gate.key}
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>

            <fieldset className="fieldset">
              <legend>Fires when the answer</legend>
              <div className="field">
                <label htmlFor={'g-in-' + i}>Is one of</label>
                <input
                  id={'g-in-' + i}
                  className="mono"
                  disabled={!editable}
                  value={(gate.answer_in ?? []).join(', ')}
                  spellCheck={false}
                  onChange={(e) => edit(i, (g) => ({ ...g, answer_in: splitList(e.target.value) }))}
                />
              </div>
              <div className="field">
                <label htmlFor={'g-notin-' + i}>Is not one of</label>
                <input
                  id={'g-notin-' + i}
                  className="mono"
                  disabled={!editable}
                  value={(gate.answer_not_in ?? []).join(', ')}
                  spellCheck={false}
                  onChange={(e) => edit(i, (g) => ({ ...g, answer_not_in: splitList(e.target.value) }))}
                />
              </div>
              <div className="field">
                <label htmlFor={'g-gt-' + i}>Is a number greater than</label>
                <input
                  id={'g-gt-' + i}
                  type="number"
                  disabled={!editable}
                  value={gate.greater_than ?? ''}
                  onChange={(e) =>
                    edit(i, (g) => ({
                      ...g,
                      greater_than: e.target.value === '' ? undefined : Number(e.target.value),
                    }))
                  }
                />
              </div>
              <label className="chip-toggle">
                <input
                  type="checkbox"
                  disabled={!editable}
                  checked={gate.when_unanswered ?? false}
                  onChange={(e) => edit(i, (g) => ({ ...g, when_unanswered: e.target.checked }))}
                />
                Or when the question was left unanswered
              </label>
            </fieldset>

            <div className="form-grid">
              <div className="field">
                <label htmlFor={'g-effect-' + i}>Effect</label>
                <select
                  id={'g-effect-' + i}
                  disabled={!editable}
                  value={gate.effect}
                  onChange={(e) => {
                    const effect = e.target.value
                    edit(i, (g) => ({ ...g, effect, band: EFFECT_NEEDS_BAND.has(effect) ? (g.band ?? 'HIGH') : undefined }))
                  }}
                >
                  {EFFECTS.map((e) => (
                    <option key={e.value} value={e.value}>
                      {e.label}
                    </option>
                  ))}
                </select>
                <p className="field-note">{EFFECTS.find((e) => e.value === gate.effect)?.detail}</p>
              </div>
              {needsBand && (
                <div className="field">
                  <label htmlFor={'g-band-' + i}>Band</label>
                  <select
                    id={'g-band-' + i}
                    disabled={!editable}
                    value={gate.band ?? 'HIGH'}
                    onChange={(e) => edit(i, (g) => ({ ...g, band: e.target.value }))}
                  >
                    {BANDS.map((b) => (
                      <option key={b} value={b}>
                        {b}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="field">
              <label htmlFor={'g-reason-' + i}>Reason</label>
              <input
                id={'g-reason-' + i}
                disabled={!editable}
                value={gate.reason}
                onChange={(e) => edit(i, (g) => ({ ...g, reason: e.target.value }))}
              />
              {gate.reason.trim() === '' && <p className="field-error">A gate with no reason cannot be promoted</p>}
            </div>

            <div className="field">
              <label htmlFor={'g-cite-' + i}>Citation</label>
              <input
                id={'g-cite-' + i}
                className="mono"
                disabled={!editable}
                value={gate.citation}
                spellCheck={false}
                placeholder="RBI-ITSO 5.2"
                onChange={(e) => edit(i, (g) => ({ ...g, citation: e.target.value }))}
              />
              {gate.citation.trim() === '' && (
                <p className="field-error">A gate with no citation cannot be promoted</p>
              )}
            </div>
          </div>
        )
      })}

      <datalist id="known-question-keys-gate">
        {knownKeys.map((k) => (
          <option key={k} value={k} />
        ))}
      </datalist>

      {editable && (
        <button
          type="button"
          onClick={() =>
            set({
              gates: [
                ...model.gates,
                {
                  key: 'gate-' + (model.gates.length + 1),
                  question_key: '',
                  answer_in: [],
                  effect: 'CAP_AT_BAND',
                  band: 'HIGH',
                  reason: '',
                  citation: '',
                },
              ],
            })
          }
        >
          Add a gate
        </button>
      )}
    </section>
  )
}

function splitList(raw: string): string[] {
  return raw
    .split(',')
    .map((v) => v.trim().toUpperCase())
    .filter(Boolean)
}

function swap<T>(list: T[], i: number, j: number): T[] {
  const a = list[i]
  const b = list[j]
  if (a === undefined || b === undefined) return list
  const next = [...list]
  next[i] = b
  next[j] = a
  return next
}
